import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setEmailFormHint } from '@store/slices/form-hints.slice';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { RxUtils } from '@utils';
import { authService } from '@services';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { AuthLayout, FormikPassword, FormikTextfield, FormikTextFieldProps, FormikSubmitButton } from '@components/common';
import { RockHandIcon } from '@components/icons';

type FormFields = Extendable<{
	email: string;
	oldPassword: string;
	newPassword: string;
	newPasswordAgain: string;
}>;

const InputStyle: FormikTextFieldProps['sx'] = { minWidth: 300, maxWidth: 300 };

export const ChangePassword: FC = () => {
	const { t } = useTranslation('common');
	const [searchParams] = useSearchParams();
	const emailFromSearchParam = searchParams.get('email');
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const formik = useFormik<FormFields>({
		initialValues: { email: emailFromSearchParam || '', oldPassword: '', newPassword: '', newPasswordAgain: '' },
		validateOnChange: true,
		validationSchema: yup.object({
			email: yup.string().email(t('validation.email-invalid')).required(t('validation.email-required')),
			oldPassword: yup.string().min(8, t('validation.password-min-char-len')).required(t('validation.password-required')),
			newPassword: yup.string().min(8, t('validation.password-min-char-len')).required(t('validation.password-required')),
			newPasswordAgain: yup
				.string()
				.required(t('validation.password-required'))
				.oneOf([yup.ref('newPassword'), null], t('validation.passwords-must-match'))
		}),
		onSubmit: ({ email, oldPassword, newPassword }, api) => {
			RxUtils.promisify(
				authService.changePassword(email, oldPassword, newPassword),
				() => navigate('/login'),
				RxUtils.noop,
				() => api.setSubmitting(false)
			);
		}
	});

	return (
		<AuthLayout>
			<Stack
				direction="column"
				spacing={{ xs: 2, md: 10 }}
				sx={{ flexGrow: 1, paddingX: 4, alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'center' } }}
			>
				<Stack direction="column" alignItems="center">
					<RockHandIcon sx={{ width: 48, height: 48, display: { xs: 'none', md: 'unset' } }} />
					<Typography
						variant="h2"
						sx={{
							marginTop: { xs: 0, md: 4 },
							fontSize: (theme) => ({ xs: theme.typography.h4.fontSize, md: theme.typography.h2.fontSize })
						}}
					>
						{t('change-password.title')}
					</Typography>
					<Stack direction="row" spacing={1} alignItems="center" sx={{ marginTop: 2 }}>
						<Typography
							sx={{ fontSize: (theme) => ({ xs: theme.typography.caption.fontSize, md: theme.typography.body1.fontSize }) }}
						>
							{t('change-password.caption')}
						</Typography>
					</Stack>
				</Stack>
				<Box component="form" onSubmit={formik.handleSubmit} sx={{ width: 'fit-content' }}>
					<Grid container direction="row" spacing={{ xs: 1, md: 4 }} sx={{ width: { xs: 1.0, md: 650 }, marginX: 'auto' }}>
						<Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
							<FormikTextfield
								formikApi={formik}
								name="email"
								title={t('email')}
								placeholder={t('email')}
								sx={InputStyle}
								containerProps={{ width: 'fit-content' }}
								onChange={(e) => dispatch(setEmailFormHint(e.target.value))}
								InputProps={{
									readOnly: !!emailFromSearchParam
								}}
							/>
						</Grid>
						<Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
							<FormikPassword
								formikApi={formik}
								name="oldPassword"
								title={t('change-password.old-password')}
								placeholder={t('change-password.old-password')}
								sx={InputStyle}
								containerProps={{ width: 'fit-content' }}
							/>
						</Grid>
						<Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
							<FormikPassword
								formikApi={formik}
								name="newPassword"
								title={t('new-password')}
								placeholder={t('new-password')}
								sx={InputStyle}
								containerProps={{ width: 'fit-content' }}
							/>
						</Grid>
						<Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
							<FormikPassword
								formikApi={formik}
								name="newPasswordAgain"
								title={t('new-password-again')}
								placeholder={t('new-password-again')}
								sx={InputStyle}
								containerProps={{ width: 'fit-content' }}
							/>
						</Grid>

						<Grid item xs={12} sx={{ marginTop: { xs: 3, md: 10 }, display: 'flex', alignItems: 'center' }}>
							<FormikSubmitButton color="primary" formik={formik} data-testid="change-password-submit-button">
								{t('change-password.change-button')}
							</FormikSubmitButton>
						</Grid>
					</Grid>
				</Box>
			</Stack>
		</AuthLayout>
	);
};
